import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import type { User, LoginCredentials, RegisterData, AuthResponse } from '@/types/auth'
import { useI18n } from 'vue-i18n'
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics'
import { trackRegistrationCompleted } from '@/utils/zaraz'
import { requestLogContext } from '@/utils/requestTracking'

type PhotoWire = Partial<import('@/types/auth').UserPhoto> & {
  id?: string | number
  url?: string
  original_url?: string
  is_primary?: boolean
  is_cover?: boolean
  order?: number
  sort_order?: number
  status?: number
  status_name?: string
}

const normalizePhoto = (photo: PhotoWire): import('@/types/auth').UserPhoto => {
  const url = photo.url || photo.original_url || ''
  const isPrimary = Boolean(photo.is_primary ?? photo.is_cover)
  const order = photo.order ?? photo.sort_order ?? 0

  return {
    id: String(photo.id ?? ''),
    filename: photo.filename || '',
    url,
    original_url: photo.original_url || url,
    thumbnail_url: photo.thumbnail_url,
    is_private: Boolean(photo.is_private),
    is_primary: isPrimary,
    is_cover: Boolean(photo.is_cover ?? isPrimary),
    order,
    sort_order: photo.sort_order ?? order,
    status: photo.status ?? 0,
    status_name: photo.status_name || '',
    created_at: photo.created_at || ''
  }
}

const normalizePhotos = (photos: unknown): import('@/types/auth').UserPhoto[] =>
  Array.isArray(photos) ? photos.map(photo => normalizePhoto(photo as PhotoWire)) : []

export const useAuthStore = defineStore('auth', () => {
  const toast = useToast()
  const { t } = useI18n()
  const router = useRouter()
  const { trackLogin, trackSignUp, trackEvent } = useGoogleAnalytics()
  
  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const isInitialized = ref(false)
  
  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isVip = computed(() => {
    // 檢查 is_vip 字段（後端返回的字段）
    if (user.value?.is_vip) return true
    
    // 備用檢查：subscription 狀態
    if (user.value?.subscription?.status === 'active') return true
    
    return false
  })
  // ACCESS_SWITCH_KEY: free_access_enabled
  // Invited beta access is a separate, temporary runtime policy. It grants
  // MVP feature access without changing a member's displayed/stored VIP status.
  const hasMvpAccess = computed(() => isVip.value || user.value?.free_access_enabled === true)
  const userType = computed(() => user.value?.account_type)
  const userProfile = computed(() => user.value?.profile)
  
  // Actions
  const login = async (credentials: LoginCredentials) => {
    try {
      isLoading.value = true
      const response = await api.post<AuthResponse>('/auth/login', credentials)
      
      if (response.data.success) {
        token.value = response.data.data.tokens?.access_token
        refreshToken.value = response.data.data.tokens?.refresh_token
        user.value = response.data.data.user

        console.log('Auth store: Login successful, setting tokens and user:', {
          tokenSet: !!token.value,
          refreshTokenSet: !!refreshToken.value,
          userSet: !!user.value,
          isAuthenticated: isAuthenticated.value
        })

        // Store tokens in localStorage for persistence
        if (token.value) {
          localStorage.setItem('auth_token', token.value)
        }
        if (refreshToken.value) {
          localStorage.setItem('refresh_token', refreshToken.value)
        }

        // Set default authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`

        // Fetch complete profile data including photos
        await fetchProfile()

        console.log('Auth store: After fetchProfile, auth state:', {
          tokenSet: !!token.value,
          userSet: !!user.value,
          isAuthenticated: isAuthenticated.value
        })

        // Track login event with Google Analytics
        trackLogin('email')

        toast.success(t('apiMessages.auth.loginSuccess'))

        return true
      } else {
        toast.error(response.data.message || t('errorMessages.auth.loginError'))
        return false
      }
    } catch (error: any) {
      const status = error.response?.status
      if (status !== 401) {
        console.error('Login error:', requestLogContext(error))
      }

      // Keep the credential failure message in the active UI locale. The API
      // may return an English message, which otherwise leaks through the
      // localized login screen.
      const message = status === 401
        ? t('errorMessages.auth.invalidCredentials')
        : error.response?.data?.message || t('errorMessages.auth.loginError')
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  const register = async (data: RegisterData, endpoint = '/auth/register') => {
    try {
      isLoading.value = true
      const response = await api.post<AuthResponse>(endpoint, data)
      
      if (response.data.success) {
        // Auto-login after successful registration
        const authData = response.data.data
        user.value = authData.user
        token.value = authData.tokens?.access_token
        refreshToken.value = authData.tokens?.refresh_token
        
        // Store tokens in localStorage for persistence
        if (token.value) {
          localStorage.setItem('auth_token', token.value)
        }
        if (refreshToken.value) {
          localStorage.setItem('refresh_token', refreshToken.value)
        }
        
        // Set auth header for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${authData.tokens?.access_token}`

        // Track registration event with Google Analytics
        const registrationMethod = endpoint.includes('google') ? 'google' : 'email'
        trackSignUp(registrationMethod)
        trackRegistrationCompleted(registrationMethod)

        toast.success(t('apiMessages.auth.registerSuccess'))
        
        // Email verification is an account flag, not a registration gate.
        await router.push('/profile/edit')
        
        return true
      } else {
        toast.error(response.data.message || t('errorMessages.auth.registrationError'))
        return false
      }
    } catch (error: any) {
      console.error('Registration error:', requestLogContext(error))
      const message = error.response?.data?.message || t('errorMessages.auth.registrationError')
      toast.error(message)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const completeGoogleLogin = async () => {
    try {
      isLoading.value = true
      const response = await api.get<AuthResponse>('/auth/google/complete')
      if (!response.data.success) return false
      const authData = response.data.data
      token.value = authData.tokens?.access_token
      refreshToken.value = authData.tokens?.refresh_token
      user.value = authData.user
      if (token.value) localStorage.setItem('auth_token', token.value)
      if (refreshToken.value) localStorage.setItem('refresh_token', refreshToken.value)
      api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
      await fetchProfile()
      trackLogin('google')
      return true
    } catch (error) {
      console.error('Google login completion failed:', requestLogContext(error))
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server
      if (token.value) {
        await api.post('/auth/logout')
      }
    } catch (error) {
      console.error('Logout error:', requestLogContext(error))
    } finally {
      // Clear local state regardless of API call result
      token.value = null
      refreshToken.value = null
      user.value = null

      // Clear stored tokens
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('sugarm_auth')
      
      // Remove authorization header
      delete api.defaults.headers.common['Authorization']
      
      toast.success(t('auth.logout_success'))
      await router.push('/auth/login')
    }
  }
  
  const refreshAuthToken = async () => {
    try {
      const currentRefreshToken = refreshToken.value || localStorage.getItem('refresh_token')
      if (!currentRefreshToken) {
        throw new Error('No refresh token available')
      }

      const response = await api.post<AuthResponse>('/auth/refresh', {
        refresh_token: currentRefreshToken
      })

      if (response.data.success) {
        const tokenData = response.data.data
        token.value = tokenData.tokens?.access_token
        refreshToken.value = tokenData.tokens?.refresh_token

        // Update localStorage
        localStorage.setItem('auth_token', token.value)
        if (refreshToken.value) {
          localStorage.setItem('refresh_token', refreshToken.value)
        }

        // Update authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`

        return true
      } else {
        await logout()
        return false
      }
    } catch (error) {
      console.error('Token refresh error:', requestLogContext(error))
      await logout()
      return false
    }
  }
  
  // Add flags to prevent multiple simultaneous API calls
  let profileFetchPromise: Promise<boolean> | null = null

  const fetchProfile = async (forceRefresh = false) => {
    // If profile fetch is already in progress, wait for it
    if (profileFetchPromise && !forceRefresh) {
      console.log('Auth store: Profile fetch already in progress, waiting...')
      return profileFetchPromise
    }

    // If we already have complete user data and not forcing refresh, skip API call
    if (!forceRefresh && user.value && (user.value.profile || user.value.age || user.value.city)) {
      console.log('Auth store: Profile data already available, skipping API call')
      return true
    }

    profileFetchPromise = (async () => {
      try {
        console.log('Auth store: Fetching user profile...')
        const response = await api.get<{ data: User }>('/users/profile')

        if (response.data && response.data.data) {
          user.value = response.data.data
          user.value.photos = normalizePhotos(user.value.photos)
          if (user.value.profile?.photos) {
            user.value.profile.photos = normalizePhotos(user.value.profile.photos)
          }

          // Also fetch photos separately and merge into user object
          try {
            console.log('Auth store: Fetching user photos...')
            const photosResponse = await api.get<{ data: any[] }>('/users/photos')
            if (photosResponse.data?.data && user.value) {
              user.value.photos = normalizePhotos(photosResponse.data.data)
              console.log('Auth store: Fetched and merged photos:', user.value.photos?.length || 0)

              // If no photos in main user object, try to add them to profile
              if (!user.value.profile) {
                user.value.profile = {}
              }
              if (!user.value.profile.photos) {
                user.value.profile.photos = user.value.photos
              }
            }
          } catch (photoError) {
            console.error('Auth store: Failed to fetch photos:', requestLogContext(photoError))
          }

          console.log('Auth store: Profile fetch successful')
          return true
        } else {
          console.error('Auth store: Invalid profile response format')
          return false
        }
      } catch (error: any) {
        console.error('Auth store: Fetch profile error:', requestLogContext(error))
        return false
      } finally {
        profileFetchPromise = null
      }
    })()

    return profileFetchPromise
  }
  
  const updateProfile = async (profileData: Partial<User>, showSuccessToast: boolean = true) => {
    try {
      isLoading.value = true
      
      const response = await api.put<{ data: User }>('/users/profile', profileData)
      
      if (response.data?.data) {
        user.value = response.data.data
        trackEvent('profile_saved', { action: 'profile_saved', category: 'onboarding' })
        
        if (showSuccessToast) {
          toast.success(t('profile.profile_updated'))
        }
        return true
      }
      return false
    } catch (error: any) {
      console.error('Update profile error:', requestLogContext(error))
      toast.error(error.response?.data?.message || t('profile.error.updateFailed'))
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  const forgotPassword = async (email: string) => {
    try {
      isLoading.value = true
      const response = await api.post('/auth/forgot-password', { email })
      
      if (response.data.success) {
        toast.success(t('auth.passwordResetEmailSent'))
        return true
      } else {
        toast.error(response.data.message || t('errorMessages.auth.resendFailed'))
        return false
      }
    } catch (error: any) {
      console.error('Forgot password error:', requestLogContext(error))
      toast.error(error.response?.data?.message || t('errorMessages.auth.resendFailed'))
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  const verifyEmail = async (token: string, userId?: string) => {
    try {
      isLoading.value = true
      
      // Build query parameters
      const params = new URLSearchParams()
      params.append('token', token)
      if (userId) {
        params.append('user_id', userId)
      }
      
      const response = await api.post(`/auth/verify-email?${params.toString()}`)
      
      if (response.data.success) {
        // Update user's email_verified status
        if (user.value) {
          user.value.email_verified = true
        }
        toast.success(t('auth.email_verified'))
        return true
      } else {
        toast.error(response.data.message || t('auth.emailVerificationFailed'))
        return false
      }
    } catch (error: any) {
      console.error('Email verification error:', requestLogContext(error))
      toast.error(error.response?.data?.message || t('auth.emailVerificationFailed'))
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  // Add a flag to prevent multiple simultaneous initialization calls
  let initializationPromise: Promise<void> | null = null

  const initializeAuth = async () => {
    if (isInitialized.value) return

    // If initialization is already in progress, wait for it
    if (initializationPromise) {
      return initializationPromise
    }

    initializationPromise = (async () => {
      try {
        console.log('Auth store: Starting initialization...')
        const storedToken = localStorage.getItem('auth_token')
        const storedRefreshToken = localStorage.getItem('refresh_token')
        console.log('Auth store: Stored tokens exist:', {
          token: !!storedToken,
          refreshToken: !!storedRefreshToken
        })

        // Handle stale persisted state (token in Pinia storage but not in localStorage)
        if (!storedToken && token.value) {
          console.warn('Auth store: Detected persisted token without auth_token; clearing stale session')
          token.value = null
          refreshToken.value = null
          user.value = null
          localStorage.removeItem('sugarm_auth')
          localStorage.removeItem('auth_token')
          localStorage.removeItem('refresh_token')
          delete api.defaults.headers.common['Authorization']
        }

        if (storedToken) {
          token.value = storedToken
          refreshToken.value = storedRefreshToken
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
          console.log('Auth store: Tokens set, fetching profile...')

          // Verify token is still valid by fetching profile
          // R2 image URLs are short-lived. A persisted Pinia profile may still
          // contain expired photo URLs, so a restored session must refresh it.
          const isValid = await fetchProfile(true)
          console.log('Auth store: Profile fetch result:', isValid)

          if (!isValid) {
            // Token is invalid, clear it and show user-friendly message
            console.log('Auth store: Token invalid, clearing and showing message...')
            user.value = null
            token.value = null
            refreshToken.value = null
            localStorage.removeItem('auth_token')
            localStorage.removeItem('refresh_token')
            localStorage.removeItem('sugarm_auth')
            delete api.defaults.headers.common['Authorization']

            // Show session expiry message to user (only if not on login/register pages)
            const currentPath = window.location.pathname
            if (!currentPath.includes('/auth/')) {
              toast.error(t('auth.sessionExpired') || 'Your session has expired. Please log in again.')
            }

            // Don't redirect here - let the router guard handle it
            // This prevents race conditions and redirect loops
          }
        } else {
          console.log('Auth store: No stored token found')
        }

        isInitialized.value = true
        console.log('Auth store: Initialization complete:', {
          isAuthenticated: isAuthenticated.value,
          hasToken: !!token.value,
          hasUser: !!user.value
        })
      } catch (error) {
        console.error('Auth store: Initialization error:', requestLogContext(error))
        isInitialized.value = true // Still mark as initialized to prevent loops
      } finally {
        initializationPromise = null
      }
    })()

    return initializationPromise
  }
  
  const checkVipStatus = () => {
    return hasMvpAccess.value
  }
  
  const getAccountTypeName = () => {
    if (!user.value?.account_type) return 'Member'
    
    const types = []
    if (user.value.account_type & 1) types.push('Sugar Daddy')
    if (user.value.account_type & 2) types.push('Sugar Mommy')
    if (user.value.account_type & 4) types.push('Sugar Baby')
    if (user.value.account_type & 8) types.push('Sugar Boy')
    
    return types.join(' / ') || 'Member'
  }
  
  return {
    // State
    user,
    token,
    isLoading,
    isInitialized,
    
    // Getters
    isAuthenticated,
    isVip,
    hasMvpAccess,
    userType,
    userProfile,
    
    // Actions
    login,
    register,
    completeGoogleLogin,
    logout,
    refreshAuthToken,
    fetchProfile,
    updateProfile,
    forgotPassword,
    verifyEmail,
    initializeAuth,
    checkVipStatus,
    getAccountTypeName
  }
}, {
  persist: {
    key: 'sugarm_auth',
    storage: localStorage,
    paths: ['token', 'refreshToken', 'user']
  }
})
